'use client'

import Image from 'next/image'
import { getPlaySettings } from '@/lib/phishtrainer/play-settings'
import { useEffect, useState } from 'react'

/**
 * Player-portal brand logo with proper resolution:
 *
 *  1. Workspace-uploaded logo (PhishTrainer Settings → branding.logoPath) wins —
 *     the customer's own company logo when they've uploaded one.
 *  2. Otherwise the HumanFirewall default wordmark, theme-aware via the `.dark`
 *     class the player ThemeContext toggles: dark theme → white logo,
 *     light theme → dark logo (no CSS-invert hacks; real assets per mode).
 */
export function PlayerBrandLogo() {
  const [uploaded, setUploaded] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getPlaySettings()
      .then((j) => {
        if (cancelled) return
        const p: string | undefined = j?.data?.branding?.logoPath
        // Defense-in-depth: never render the cross-tenant Wipro mock asset even
        // if it somehow slips through the play-scoped settings response.
        if (p && p.trim() && !p.toLowerCase().includes('wipro')) {
          // Absolute (http / leading slash) → as-is; otherwise root-relative.
          setUploaded(/^(https?:)?\//.test(p) ? p : `/${p}`)
        }
      })
      .catch(() => {
        // Keep the default logo on error.
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (uploaded) {
    // Customer-uploaded logo — single asset, shown as-is on any theme.
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={uploaded} alt="Logo" className="h-7 w-auto object-contain" />
  }

  // HumanFirewall default — swap the real asset by theme (CSS, no JS flash).
  return (
    <>
      <Image
        src="/brand/humanfirewall-logo-light.svg"
        alt="HumanFirewall"
        width={176}
        height={28}
        priority
        className="block h-7 w-auto dark:hidden"
      />
      <Image
        src="/brand/humanfirewall-logo.svg"
        alt="HumanFirewall"
        width={176}
        height={28}
        priority
        className="hidden h-7 w-auto dark:block"
      />
    </>
  )
}
